import { useQuery } from '@tanstack/react-query';
import { Button, Drawer } from '@toggle/design-system';
import { formatTicker } from '@toggle/toggle';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ExpandableDrawer } from '~/components/expandable-drawer/ExpandableDrawer';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { createEntityQueryOptions } from '~/react-query-hooks/entities/useEntities.utils';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { DownloadButton } from '~/views/stream/components/download-button/DownloadButton';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { handleDownloadDrawer } from '~/views/stream/utils/handleDownloadDrawer';
import { useCatalystDrawer } from '~/widgets/catalyst-insights-drawer/useCatalystDrawer';
import { EntityHeaderWatchlist } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';

import { CompanyCatalystWidget } from '../../company-catalyst-widget/CompanyCatalystWidget';
import styles from './CompanyCatalystDrawer.module.scss';
import { EntityFullViewDrawer } from './full-view-drawer/EntityFullViewDrawer';

const COMPANY_CATALYST_DRAWER_WIDTH = 740;

export const CompanyCatalystDrawer = () => {
  const { t } = useTranslation(['insights']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const { trackAssetOpen, trackCatalystDrawerOpen } = usePosthogEvents();
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const [expandCitations, setExpandCitations] = useState(false);

  const { closeDrawer, isOpen, drawerType, entityTag, insightId, utmSource } =
    useCatalystDrawer();

  /*
   * Separate entity query when tag is unchanged,
   * so we can keep the expanded chart/subheader stable
   * between insights for the same entity
   */
  const {
    data: entityQueryData,
    isLoading: isEntityLoading,
    isFetching: isEntityFetching,
    isPending: isEntityPending,
  } = useQuery({
    ...createEntityQueryOptions(entityTag ?? ''),
    placeholderData: previousData =>
      previousData?.data?.tag === entityTag ? previousData : undefined,
  });
  const entityFromEntityQuery = entityQueryData?.data;

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && utmSource === POSTHOG_TRACKING_SOURCES.EMAIL) {
      trackCatalystDrawerOpen(utmSource, 'company');
    }
  }, [isOpen, utmSource, drawerType]);

  const handleHeaderButtonClick = () => {
    if (entityTag) {
      trackAssetOpen(
        POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER,
        entityTag
      );
      navigateToPage({
        entityTag: entityTag,
      });
    }
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        entityTag: entityTag,
        expandAccordions: setExpandCitations,
      });
    }
  };

  if (!insightId || !entityTag) {
    return null;
  }

  const headerButton = (
    <div className={styles.headerButtons}>
      <Button
        variant="empty"
        label={t('insights:catalyst.viewOnAO')}
        iconName="Expand"
        size="xsmall"
        onClick={handleHeaderButtonClick}
      />
      <DownloadButton onClick={handleDownload} />
    </div>
  );

  const getContent = () => (
    <div ref={contentWrapperRef} className={styles.contentWrapper}>
      <ErrorBoundaryWrapper>
        <CompanyCatalystWidget
          insightId={insightId}
          expandCitations={expandCitations}
        />
      </ErrorBoundaryWrapper>
      <ScheduleDemoBanner />
    </div>
  );

  if (
    isUniversalDrawerEnabled &&
    entityFromEntityQuery &&
    !isBasicOrLightUser
  ) {
    return (
      <ExpandableDrawer
        isOpen={isOpen}
        handleCloseClick={closeDrawer}
        headerButton={headerButton}
        defaultWidth={COMPANY_CATALYST_DRAWER_WIDTH}
        label={t('insights:viewAllSection.whatElseIsMoving', {
          name: formatTicker(entityFromEntityQuery),
        })}
        subHeaderNode={
          <EntityHeaderWatchlist
            entity={entityFromEntityQuery}
            logoSize={48}
            className={styles.styledEntityHeaderWatchlist}
            isFetching={isEntityFetching}
          />
        }
        fullViewContent={
          <EntityFullViewDrawer
            trackingSource={POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER}
            entity={entityFromEntityQuery}
            isEntityLoading={isEntityLoading}
            isEntityPending={isEntityPending}
          />
        }
      >
        {getContent()}
      </ExpandableDrawer>
    );
  }

  return (
    <Drawer
      className={styles.styledDrawer}
      data-testid="catalyst-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={headerButton}
    >
      {getContent()}
    </Drawer>
  );
};
